/** Right-click context menu for an existing Canvas node (Function Card V2 §16-§20).
 *
 * Portaled to `document.body` with the same opaque dark shell as the Add Node
 * menu — never rendered inside the media viewport subtree. */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

const VIEWPORT_PAD = 8

interface Props {
  x: number
  y: number
  canExpand: boolean
  isExpanded: boolean
  onClose: () => void
  onDuplicate: () => void
  onDelete: () => void
  onToggleExpand: () => void
}

export default function CanvasNodeContextMenu({
  x,
  y,
  canExpand,
  isExpanded,
  onClose,
  onDuplicate,
  onDelete,
  onToggleExpand,
}: Props) {
  const { t } = useTranslation('canvas')
  const shellRef = useRef<HTMLDivElement>(null)
  const rootScrollRef = useRef<HTMLDivElement>(null)
  const [shellStyle, setShellStyle] = useState<React.CSSProperties>({ left: x, top: y })
  const [rootMaxHeight, setRootMaxHeight] = useState<number | undefined>(undefined)

  const positionShell = useCallback(() => {
    const shell = shellRef.current
    if (!shell) return
    const vw = window.innerWidth
    const vh = window.innerHeight
    const rootScrollHeight = rootScrollRef.current?.offsetHeight ?? 0
    const rect = shell.getBoundingClientRect()
    const naturalHeight = rect.height || 120
    const chrome = Math.max(0, naturalHeight - rootScrollHeight)
    const availableHeight = vh - VIEWPORT_PAD * 2
    const height = Math.min(naturalHeight, availableHeight)
    const width = rect.width || 200

    let top = y
    if (y + height > vh - VIEWPORT_PAD) {
      const availableAbove = y - VIEWPORT_PAD
      const availableBelow = vh - VIEWPORT_PAD - y
      top =
        availableAbove > availableBelow
          ? Math.max(VIEWPORT_PAD, y - height)
          : Math.max(VIEWPORT_PAD, vh - VIEWPORT_PAD - height)
    }
    top = Math.max(VIEWPORT_PAD, Math.min(top, vh - VIEWPORT_PAD - height))

    let left = x
    if (left + width > vw - VIEWPORT_PAD) {
      left = Math.max(VIEWPORT_PAD, vw - VIEWPORT_PAD - width)
    }
    left = Math.max(VIEWPORT_PAD, left)

    setShellStyle({ left, top })
    setRootMaxHeight(naturalHeight > availableHeight ? Math.max(0, availableHeight - chrome) : undefined)
  }, [x, y])

  useLayoutEffect(() => {
    positionShell()
    const id = window.requestAnimationFrame(() => positionShell())
    return () => window.cancelAnimationFrame(id)
  }, [positionShell])

  useEffect(() => {
    const onResize = () => positionShell()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [positionShell])

  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return
      const target = e.target as Node | null
      if (shellRef.current?.contains(target)) return
      onClose()
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', onMouseDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [onClose])

  return createPortal(
    <div
      className="pcv-context-menu-shell pcv-node-context-menu-shell"
      style={{ position: 'fixed', zIndex: 10050, ...shellStyle }}
      ref={shellRef}
      data-testid="canvas-node-context-menu"
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="pcv-context-menu pcv-context-menu-root pcv-node-context-menu">
        <div className="pcv-context-menu-title">{t('nodeContextMenu.title')}</div>
        <div ref={rootScrollRef} className="pcv-context-menu-root-scroll" style={{ maxHeight: rootMaxHeight }}>
          {canExpand ? (
            <button
              type="button"
              className="pcv-context-menu-item"
              data-testid="node-context-expand"
              onClick={() => {
                onToggleExpand()
                onClose()
              }}
            >
              {isExpanded ? t('nodeContextMenu.collapse') : t('nodeContextMenu.expand')}
            </button>
          ) : null}
          <button
            type="button"
            className="pcv-context-menu-item"
            data-testid="node-context-duplicate"
            onClick={() => {
              onDuplicate()
              onClose()
            }}
          >
            {t('nodeContextMenu.duplicate')}
          </button>
          <button
            type="button"
            className="pcv-context-menu-item pcv-context-menu-item--danger"
            data-testid="node-context-delete"
            onClick={() => {
              onDelete()
              onClose()
            }}
          >
            {t('nodeContextMenu.delete')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
